import React, { Fragment, ReactNode } from 'react';
import {
  AnyAssetApplication,
  AssetParams,
  Connections,
  ConnectionTypes,
  Flex,
  FormikFormAsset,
  FormikNewFormValues,
  Grid,
  NONE_SELECT,
  NoneSelect,
  openNotificationWithIcon,
  PageContent,
  Segment,
  useAssetConfig,
  useAssetParams,
  useAssetTranslation,
} from '@mss/mss-component-library';
import { notification, Spin } from 'antd';
import { Form, useFormikContext } from 'formik';
import Button from '../../Button';
import ConnectionResults from './ConnectionResults';
import { testConnection } from '../services/testConnection';
import { configType } from '../utils/conversions';
import { mapInputToJSX } from '../utils/mapInputToJSX';
import { SelectInput } from './Inputs';
import { SNMPCredentialsForm } from './SNMPCredentialsForm';
import { emptyConnectionTest } from '../utils/emptyConnectionTest';
import { resultPath } from '../utils/resultPath';
import { fieldNameToFormName } from './inputUtil';

type Props = {
  compact?: boolean;
  title?: string;
  index?: number;
  disabled?: boolean;
  saveButton?: React.ReactNode;
  autoSave?: () => Promise<void>;
  overrideAssetParams?: Partial<AssetParams>;
  disableNotification?: boolean;
};

type ConnectionContainerProps = Props & {
  children: ReactNode;
  resultKey?: string;
  testType: ConnectionTypes | NoneSelect;
};

type ConnectionInputsProps = {
  index?: number;
  disabled?: boolean;
  compact?: boolean;
  type: ConnectionTypes;
  overrideAssetParams?: Partial<AssetParams>;
};

function ConnectionInputs<AssetType extends AnyAssetApplication>({
  index = 0,
  disabled,
  compact,
  type,
  overrideAssetParams,
}: ConnectionInputsProps) {
  const { assetSubType } = useAssetParams(overrideAssetParams);

  const {
    config: {
      listView: { subTypeKey },
    },
    matchingConfig: {
      type: assetSubTypeConfig,
      form: { connect },
    },
  } = useAssetConfig<AssetType>(assetSubType);

  return (
    <Grid.Row className={'mt-4'}>
      {connect[type] &&
        connect[type].inputs?.map(
          mapInputToJSX({
            index,
            disabled,
            compact,
            copyButton: true,
            subType: {
              key: subTypeKey as string,
              value: assetSubTypeConfig,
            },
          })
        )}
    </Grid.Row>
  );
}

function ConnectionContainer<AssetType extends AnyAssetApplication>({
  compact,
  title,
  children,
  index = 0,
  testType,
  saveButton,
  autoSave,
  overrideAssetParams,
  disableNotification,
}: ConnectionContainerProps) {
  const [isTesting, setIsTesting] = React.useState(false);
  const { values, setFieldValue, errors, touched } =
    useFormikContext<FormikNewFormValues<AssetType>>();
  const { assetType, assetSubType, assetId } =
    useAssetParams(overrideAssetParams);
  const { matchingConfig } = useAssetConfig<AssetType>(assetSubType);
  const noConnectionTest = testType === NONE_SELECT;
  const connectionTestValues = !noConnectionTest
    ? values.assets[index][resultPath[testType]]
    : null;
  const testPath = !noConnectionTest
    ? `assets.${index}.${resultPath[testType]}`
    : null;
  const { t } = useAssetTranslation();
  const testConnectionConfig = !noConnectionTest
    ? matchingConfig.form.connect[testType]?.testConnection
    : null;
  const inputs = !noConnectionTest
    ? matchingConfig.form.connect[testType]?.inputs
    : null;

  const testFn = async () => {
    if (noConnectionTest || !testConnectionConfig) return;
    setIsTesting(true);
    const password = (values.assets[index].values as any)['password'];
    try {
      setFieldValue(`${testPath}.loading`, true);
      const result = await testConnection({
        ...testConnectionConfig,
        type: testType,
        assetParams: {
          assetType,
          assetSubType,
        },
        index,
        body: Object.entries(values.assets[index].values).reduce(
          (acc, [key, val]: [string, any]) => {
            const isEmpty = !!(
              testConnectionConfig.cleanEmpty &&
              (val == null || val === '')
            );

            if (!isEmpty && testConnectionConfig.valueKeys.includes(key)) {
              return { ...acc, [key]: val };
            } else return acc;
          },
          {} as { [key: string]: any }
        ),
      });

      setFieldValue(`${testPath}.result`, result);

      const someFailed = Object.values(result.status).some(x => x === false);

      if (autoSave && !someFailed) {
        await autoSave();
        setFieldValue(`assets.${index}.values.password`, password, true);
      }
      if (someFailed && !disableNotification)
        openNotificationWithIcon(
          notification.warning,
          t('Connection test failed'),
          t(
            assetId
              ? 'If this is an expected result, you can still save the credentials to the asset with the "Save despite failed tests" button.'
              : 'If this is an expected result, you can still save the credentials to the new asset with the button that has appeared next to the "Test connection" button'
          )
        );
    } catch (e: any) {
      const [firstError] = Object.values(e.cause ?? {});
      const error = String(firstError ?? '');
      const isThrottling = error.startsWith(
        'You have reached the maximum allowed number of connections.'
      );
      setFieldValue(`${testPath}.result`, {
        message: firstError ?? t('Connection timed out'),
        status: {
          port: false,
          account: null,
          query: null,
        },
      });
      openNotificationWithIcon(
        notification.error,
        isThrottling
          ? t('Test connection locked for one minute')
          : t('Connection test error'),
        isThrottling
          ? t(error)
          : t(
            'The connection test could not be completed, please try again later. Alternatively, you can still save the credentials to the new asset with the button that has appeared next to the "Test connection" button'
          )
      );
    } finally {
      setFieldValue(`${testPath}.loading`, false);
    }
  };

  const hasErrorValues =
    testConnectionConfig?.valueKeys.some(
      // @ts-ignore
      x => !!errors?.assets?.[index]?.values?.[x]
    ) ?? false;

  const hasTouchedValues =
    testConnectionConfig?.valueKeys.some(
      // @ts-ignore
      x => !!touched?.assets?.[index]?.values?.[x]
    ) ?? false;

  const notEveryFieldHasValue =
    inputs
      ?.filter(x => x.required)
      .some(
        x => !values?.assets[index].values[x.fieldName as keyof AssetType]
      ) ?? false;
  if (noConnectionTest || !connectionTestValues) {
    return null;
  }
  return (
    <Grid.Row>
      <Grid.Col width={compact ? 12 : 6}>
        <Segment border={!compact} padding={!compact}>
          <Form
            onKeyDown={async e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                await testFn();
              }
            }}
          >
            {title && <h5 className={compact ? '' : 'mt-4 mb-2'}>{title}</h5>}
            {children}
            <Flex h="end" v="center">
              {saveButton ?? null}
              <Button
                variant="primary"
                disabled={
                  connectionTestValues.loading ||
                  hasErrorValues ||
                  (!hasTouchedValues && !isTesting) ||
                  notEveryFieldHasValue
                }
                onClick={testFn}
                data-ta="testCredentialsButton"
                className="ml-2"
              >
                {t('Test connection')}
              </Button>
              {notEveryFieldHasValue &&
                !disableNotification &&
                !!values.assets[0].values.id && (
                  <span className={'absolute -bottom-6 text-xs text-danger'}>
                    {t('Not all required fields are filled in')}
                  </span>
                )}
            </Flex>
          </Form>
        </Segment>
      </Grid.Col>
      <Grid.Col width={compact ? 12 : 6}>
        <Segment border={!compact} padding={!compact}>
          <Flex h="start" v="center" className="my-4">
            <h5 className="m-0">{t('Results')}</h5>
            <small className="p-1 ml-2" style={{ background: 'lightgrey' }}>
              {values.assets[index].values.ip_address}
            </small>
          </Flex>

          <Spin
            tip={`${t('Testing')}...`}
            spinning={connectionTestValues.loading}
          >
            <ConnectionResults
              index={index}
              compact={compact}
              resultParentPath={resultPath[testType]}
            />
          </Spin>
        </Segment>
      </Grid.Col>
    </Grid.Row>
  );
}

function Connection<AssetType extends AnyAssetApplication>({
  compact,
  index = 0,
  disabled,
  saveButton,
  autoSave,
  overrideAssetParams,
  disableNotification,
}: Props) {
  const { t } = useAssetTranslation();
  const { assetSubType, assetId } = useAssetParams(overrideAssetParams);
  const { config, matchingConfig } = useAssetConfig<AssetType>(assetSubType);
  const { values, setFieldValue, setFieldTouched } = useFormikContext<any>();
  const connectionTypes = Object.keys(matchingConfig.form.connect) as [
    keyof Connections,
  ];

  const hasId = !!values.assets[index].values.id;
  const selectableConfigs = connectionTypes.filter(
    x => !!matchingConfig.form.connect[x]?.select
  );
  const alwaysOnConfigs = connectionTypes.filter(
    x => !matchingConfig.form.connect[x]?.select
  );
  const hasSelectables = selectableConfigs.length > 0;
  const firstSelectableConfig =
    matchingConfig.form.connect[selectableConfigs[0]]?.select;
  const {
    label: connectionSelectLabel = '',
    key: connectionSelectKey,
    nullable,
  } = firstSelectableConfig ?? {};
  const selectedConnectionType =
    connectionSelectKey && values.assets[index].values[connectionSelectKey];

  const emptyFieldNamesTypeSwitch = selectableConfigs.reduce(
    (acc, type) => {
      const inputFieldNames =
        matchingConfig.form.connect[type]!.inputs?.map(
          input => input.fieldName
        ) ?? [];
      inputFieldNames.forEach(fieldName => {
        if (fieldName === 'ssh_hostname') return acc;
        if (fieldName === 'ssh_port') return (acc[fieldName] = 22);
        if (fieldName === 'telnet_port') return (acc[fieldName] = 23);
        return (acc[fieldName] = '');
      });
      return acc;
    },
    {} as { [key: string]: string | number }
  );

  if (Object.keys(matchingConfig.form.connect).length < 1) {
    return (
      <PageContent title={t('Connection Settings')}>
        <p>{t('No connection settings available')}</p>
      </PageContent>
    );
  }
  return (
    <PageContent
      title={
        hasId
          ? t('Connection Settings')
          : t(`Connect to ${configType(config.listView.assetType)}`)
      }
    >
      {alwaysOnConfigs.map(testType => (
        <Fragment key={testType}>
          <ConnectionContainer
            compact={compact}
            title={t('Credentials')}
            index={index}
            testType={testType}
            saveButton={saveButton}
            autoSave={autoSave}
            overrideAssetParams={overrideAssetParams}
            disableNotification={disableNotification}
          >
            {testType === 'snmp' && (
              <SNMPCredentialsForm
                formIndex={index}
                compact={compact}
                disabled={
                  disabled || values.assets[index][resultPath[testType]].loading
                }
              />
            )}
            {testType !== 'snmp' && (
              <ConnectionInputs
                compact={compact}
                disabled={
                  disabled || values.assets[index][resultPath[testType]].loading
                }
                index={index}
                type={testType}
                overrideAssetParams={overrideAssetParams}
              />
            )}
          </ConnectionContainer>
        </Fragment>
      ))}
      {hasSelectables && connectionSelectKey && (
        <>
          <Grid.Row
            className={`mb-4 ${alwaysOnConfigs.length > 0 ? 'mt-4' : ''}`}
          >
            <Grid.Col width={12}>
              <Segment border={!compact} padding={!compact}>
                <Grid.Row className={'mt-3'}>
                  <SelectInput
                    onChange={async val => {
                      setFieldValue(
                        `assets.${index}.test_${selectedConnectionType}`,
                        emptyConnectionTest
                      );
                      setFieldValue(
                        `assets`,
                        values.assets.map((asset: FormikFormAsset<any>) => {
                          if (
                            asset.values.tempId === index ||
                            asset.values.id === assetId
                          ) {
                            return {
                              ...asset,
                              values: {
                                ...asset.values,
                                ...emptyFieldNamesTypeSwitch,
                                [connectionSelectKey]: val,
                              },
                            };
                          }
                          return asset;
                        }),
                        true
                      );
                      await Promise.all(
                        Object.keys(emptyFieldNamesTypeSwitch).map(fieldName =>
                          setFieldTouched(
                            fieldNameToFormName(fieldName, index),
                            false,
                            false
                          )
                        )
                      );
                    }}
                    showSearch={false}
                    fieldName={connectionSelectKey}
                    required
                    placeholder={t('Please select one...')}
                    disabled={disabled}
                    label={connectionSelectLabel}
                    index={index}
                    value={values.assets[index].values[connectionSelectKey]}
                    options={[
                      ...selectableConfigs.map(x => ({
                        value: x,
                        label: x,
                      })),
                      ...(nullable
                        ? [{ value: NONE_SELECT, label: t(NONE_SELECT) }]
                        : []),
                    ]}
                  />
                </Grid.Row>
                {selectedConnectionType !== NONE_SELECT && (
                  <ConnectionContainer
                    compact={compact}
                    title={t('Credentials')}
                    index={index}
                    testType={values.assets[index].values[connectionSelectKey]}
                    saveButton={saveButton}
                    overrideAssetParams={overrideAssetParams}
                    disableNotification={disableNotification}
                    autoSave={alwaysOnConfigs?.length ? undefined : autoSave}
                  >
                    <ConnectionInputs
                      compact={compact}
                      disabled={disabled}
                      index={index}
                      type={selectedConnectionType}
                      overrideAssetParams={overrideAssetParams}
                    />
                  </ConnectionContainer>
                )}
              </Segment>
            </Grid.Col>
          </Grid.Row>
        </>
      )}
    </PageContent>
  );
}

export default Connection;
